/**
 * `progress` — a battery bar over the status columns the settings name.
 *
 * monday returns `value: null` and `text: ""` for every row `[cvs§2 progress]`: the number
 * only exists in the UI, and it is computed from the **done** state of the related status
 * columns. The percentage therefore comes from the sibling cells the renderer already has,
 * through the shared folder's `progressPercentage`, and the cell is read-only.
 */
import {
  findColumnType,
  isDoneLabel,
  progressPercentage,
  progressSources,
  type ProgressSettings,
  type ProgressValue,
  type StatusSettings,
  type StatusValue,
} from '@elizmonday/shared';
import { defineColumnUi } from '../types';
import type { BoardColumn, ItemRow } from '../../data/types';
import { ProgressSettingsDialog } from './SettingsDialog';

/** "Is that status cell on a done label?" — only the status column's own settings know. */
function doneOf(item: ItemRow, columns: readonly BoardColumn[]): (columnId: string) => boolean | undefined {
  return (columnId) => {
    const column = columns.find((entry) => entry.id === columnId);
    if (column === undefined || column.type !== 'status') return undefined;
    const definition = findColumnType('status');
    if (definition === undefined) return undefined;
    const settings = definition.fromMondaySettings(column.settings ?? {}) as StatusSettings;
    const cell = item.values[columnId] as StatusValue | null | undefined;
    if (cell === null || cell === undefined) return false;
    return isDoneLabel(settings, cell.index);
  };
}

export const progressColumnUi = defineColumnUi<ProgressValue, ProgressSettings>({
  type: 'progress',
  icon: 'columnStatus',
  gallery: { group: 'advanced', keywords: ['progress', 'battery', 'tracking', 'percent'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'center',
  groupable: false,

  Cell: ({ settings, item, column, context }) => {
    const columns = context.columns ?? [];
    const percentage = progressPercentage(settings, doneOf(item, columns));
    if (progressSources(settings).length === 0) {
      return (
        <span className="ez-cell__text" data-align="center" title="No status column is tracked yet">
          —
        </span>
      );
    }
    return (
      <span
        className="ez-progress"
        role="img"
        aria-label={`${String(percentage ?? 0)}%`}
        data-testid={`progress-${column.id}-${item.id}`}
      >
        <span className="ez-progress__bar" style={{ width: `${String(percentage ?? 0)}%` }} />
        <span className="ez-progress__label">{`${String(percentage ?? 0)}%`}</span>
      </span>
    );
  },

  SettingsDialog: ProgressSettingsDialog,

  fromClipboard() {
    throw new Error('the progress column is calculated');
  },
  toClipboard() {
    // monday's own `text` is `""` for every row `[cvs§2 progress]`.
    return '';
  },
  bucketsOf() {
    return [];
  },
});
